import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Currency } from '@/constants/currency'
import { BusinessRoute } from '@/constants/paths'
import {
  BankEligibility,
  BankEligibilityDocumentState,
} from '@/domains/Business/types'
import { useErrorToast } from '@/hooks/useErrorToast'
import { formatCurrency } from '@/lib/money'
import {
  Button,
  DataTable,
  SlideInScreen,
  StickyContainer,
  Typography,
} from '@/shared/ui'

import { AccountType } from '../../Accounts/types'
import { initiateBankAccountOnboarding } from '../api'

import { useOwnersAddressTableColumns } from './OwnersAddressTable/useOwnersAddressTableColumns'
import { ProofOfAddressInfoDialog } from './ProofOfAddressInfoDialog'

type Props = {
  bankEligibility?: BankEligibility
  onOwnerClick: (ownerId: string) => void
}

export const ProofOfAddressScreen = ({
  bankEligibility,
  onOwnerClick,
}: Props) => {
  const intl = useIntl()
  const notifyError = useErrorToast()
  const navigate = useNavigate()

  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(
    bankEligibility?.currentStepDetails.data.beneficialOwners.every(
      (owner) => !owner.document,
    ) ?? false,
  )

  const columns = useOwnersAddressTableColumns({
    onClick: onOwnerClick,
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: initiateBankAccountOnboarding,
  })

  const isCompleted =
    bankEligibility?.currentStepDetails.state ===
    BankEligibilityDocumentState.COMPLETED

  const handleSubmit = async () => {
    try {
      await mutateAsync(AccountType.EU_LOCAL)

      toast.success(
        intl.formatMessage({
          defaultMessage: 'Application submitted successfully',
          id: 'kyb.toast.applicationSubmitted',
        }),
      )

      navigate(BusinessRoute.Accounts)
    } catch (error) {
      notifyError(error)
    }
  }

  return (
    <>
      <SlideInScreen className="max-w-3xl">
        <Typography variant="h3" text="center">
          <FormattedMessage
            defaultMessage="Owner's proof of address"
            id="kyb.proofOfAddress.title"
          />
        </Typography>

        <div className="p-2" />

        <Typography text="center">
          <FormattedMessage
            defaultMessage="Provide a utility bill or personal bank statement to support each owner's address"
            id="kyb.proofOfAddress.subtitle"
          />
        </Typography>

        <div className="p-6" />

        <DataTable
          loaderOptions={{ rows: 1 }}
          columns={columns}
          data={bankEligibility?.currentStepDetails.data.beneficialOwners ?? []}
          isLoading={false}
        />
      </SlideInScreen>
      <StickyContainer className="mx-auto max-w-xl">
        <Button
          loading={isPending}
          disabled={!isCompleted || isPending}
          onClick={handleSubmit}
          width="full"
        >
          <FormattedMessage
            defaultMessage="Submit to apply for {currency} accounts!"
            id="action.submitToApplyForCurrencyAccounts"
            values={{ currency: formatCurrency(Currency.EURC) }}
          />
        </Button>
      </StickyContainer>

      <ProofOfAddressInfoDialog
        isOpen={isInfoDialogOpen}
        onOpenChange={setIsInfoDialogOpen}
      />
    </>
  )
}
